import React from "react";
import { useGetAotdByNumberQuery } from "../printsApi";
import type { GatheredSet, PrintPreorder } from "../printsApi";
import { preorderErrorText } from "../usePreorders";
import { useGathering } from "../useGathering";
import type { Gathering } from "../useGathering";
import { PrintFaceThumb } from "../showcase/PrintFaceThumb";

// The member's gathered trading cards: one card a day, laid out next to the empty places still to
// fill up to 21, then "pre-order the set" once there are enough. Sets already pre-ordered are
// listed under them. Shown only to a signed-in member who has gathered something.

const CARD_PX = 56;

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const shortDate = (iso: string) => {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

const Card: React.FC<{ card: PrintPreorder; g: Gathering }> = ({ card, g }) => {
  const { data: day } = useGetAotdByNumberQuery(card.number);
  const original = card.edition === "original";
  return (
    <li className={`tp-gcard ${original ? "is-og" : ""}`}>
      <span className="tp-gcard-face">
        {day ? <PrintFaceThumb day={day} format="trading-card" edition={card.edition} width={CARD_PX} /> : null}
      </span>
      <b>#{card.number}</b>
      {original && <span className="tp-gcard-og">original</span>}
      <button type="button" className="tp-link-btn" disabled={g.busy} onClick={() => g.putBack(card)}>
        put back
      </button>
    </li>
  );
};

const SetRow: React.FC<{ set: GatheredSet; g: Gathering }> = ({ set, g }) => (
  <li className="tp-set-row">
    <span className="tp-mine-what">
      <b>set of {set.cardCount} cards</b>
      <span>
        pre-ordered {shortDate(set.createdAt)} · #{Math.min(...set.cards.map((c) => c.number))} to #
        {Math.max(...set.cards.map((c) => c.number))}
      </span>
    </span>
    <button type="button" className="tp-link-btn" disabled={g.busy} onClick={() => g.removeSet(set)}>
      remove
    </button>
  </li>
);

export const GatheredCards: React.FC = () => {
  const g = useGathering();
  if (!g.signedIn || (g.cards.length === 0 && g.sets.length === 0)) return null;
  const count = g.cards.length;
  const toGo = Math.max(0, g.needed - count);

  return (
    <section className="tp-mine tp-gathered" aria-labelledby="tp-gathered-title">
      <h2 id="tp-gathered-title" className="tp-h2">
        your gathered cards
      </h2>
      <p className="tp-note">
        <b>
          {count} of {g.needed}
        </b>{" "}
        {toGo > 0
          ? `· ${toGo} more and you can pre-order them as a set. one card a day, from today's prints.`
          : "· enough for a set. pre-order them together, or keep gathering and make the set bigger."}
      </p>
      {count > 0 && (
        <ul className="tp-gathered-grid" aria-label={`${count} gathered cards`}>
          {g.cards.map((c) => (
            <Card key={c.id} card={c} g={g} />
          ))}
          {Array.from({ length: toGo }, (_, i) => (
            <li key={`empty-${i}`} className="tp-gcard is-empty" aria-hidden="true">
              <span className="tp-gcard-face" />
            </li>
          ))}
        </ul>
      )}
      {count >= g.needed && (
        <button type="button" className="sf-buy tp-set-btn" disabled={g.busy} onClick={() => g.preorderSet()}>
          <span className="sf-stretch">PRE-ORDER THE SET ({count})</span>
        </button>
      )}
      {g.sets.length > 0 && (
        <>
          <h3 className="tp-gathered-sub">your sets</h3>
          <p className="tp-note">they go into the next print run once cards are on sale. removing a set is final.</p>
          <ul className="tp-mine-list">
            {g.sets.map((s) => (
              <SetRow key={s.id} set={s} g={g} />
            ))}
          </ul>
        </>
      )}
      {g.error ? <p className="tp-error">{preorderErrorText(g.error)}</p> : null}
    </section>
  );
};

export default GatheredCards;
